import * as React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import { connect, useSelector } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { bindActionCreators } from 'redux';
import { Redirect, useParams } from 'react-router-dom';

import { Button } from '@material-ui/core';

import * as ProgramStore from '../../app/store/ProgramStore';
import urls from '../../app/urls';
import {
    TrackActivity, InfoMessage, PageTitle, PageLayout,
} from '../../common/components';
import { DelayedFetch } from '../../youwell-common/components';
import MyTaskContent from './components/MyTaskContent';
import TaskNavigation from './components/TaskNavigation';
import { getBreadCrumb } from '../../common/breadcrumbUtil';
import { listFromCommaStrings } from '../../youwell-common/stringUtils';
import { getTask } from '../../common/taskUtils';

const styles = theme => ({
    previousResponses: {
        marginTop: theme.spacing(2),
    },
    navigation: {

    },
    print: {
        display: 'flex',
        justifyContent: 'center',

        '@media print': {
            display: 'none',
        },
    },
});

export const TaskPageRedirect = () => {
    const {
        elementGroup, elementIndex,
    } = useParams();

    const currentProgram = useSelector(state => state.programStore.currentProgram);

    const task = getTask(currentProgram, elementGroup, elementIndex);

    if (!task) {
        return <InfoMessage messageKey="noTask" />;
    }

    if (currentProgram.isActiveProgram && task.module) {
        const firstNotVisited = task.module.tasks?.findIndex(d => !d.visited);
        const firstNotCompleted = task.module.tasks?.findIndex(d => !d.completed);

        let latestIndex = 0;
        if (firstNotCompleted < 0) { // All tasks completed (and then also visited), go to first task
            latestIndex = 0;
        } else if (firstNotVisited === firstNotCompleted) { // Last visited page is also completed. Go to that page (visited - 1)
            latestIndex = firstNotVisited - 1;
        } else if (firstNotVisited > firstNotCompleted) { // visited many but not completed all along the way, go to first not completed
            latestIndex = firstNotCompleted;
        } else if (firstNotVisited < 0) { // all tasks visited, but not all completed, go to first not completed
            latestIndex = firstNotCompleted;
        }

        if (latestIndex < 0) {
            latestIndex = 0;
        }

        return (<Redirect to={`${urls.inside.program}/${elementGroup}/${elementIndex}/${latestIndex}`} />);
    }

    return (<Redirect to={`${urls.inside.program}/${elementGroup}/${elementIndex}`} />);
};

const oldSystemTaskLabels = {
    subtask: 'subtask',
    hiddenSubtask: 'hiddenSubtask',
};

// For backward compatibility.. Old subtask-labels in use by Arbeid og Helse, Gynea, Adhd, Depresjon mm..
const includeOldSubtasksFromLabel = (settings, labels) => {
    if (labels) {
        const labelList = listFromCommaStrings(labels);
        return {
            ...settings,
            subtask: labelList.indexOf(oldSystemTaskLabels.subtask) > -1,
            hiddenInMenu: labelList.indexOf(oldSystemTaskLabels.hiddenSubtask) > -1,
        }
    }
    return settings;
}

class Task extends React.Component {
    taskContentRef = React.createRef()

    visitTask = task => () => {
        const { currentProgram } = this.props;

        if (currentProgram.isActiveProgram && !task.visited) {
            this.props.visitTask({
                ppeId: task.patientProgramElementId,
                id: task.id,
            }, { reducerParams: { patientProgramId: currentProgram.id, taskId: task.id } });
        }
    }

    render() {
        const {
            classes, translate, currentProgram, match, clientConfig,
        } = this.props;
        const { elementGroup, elementIndex, subIndex } = match.params;

        const task = getTask(currentProgram, elementGroup, elementIndex, subIndex);

        if (!task) {
            return <InfoMessage messageKey="noTask" />;
        }

        const sidemenu = task.showSideMenu ? {
            currentIndex: task.currentTaskNr,
            title: task.moduleName,
            menuItems: task.module.tasks.map((t, i) => ({
                text: t.name,
                settings: includeOldSubtasksFromLabel(t.settings, t.label),
                url: `${urls.inside.program}/${task.elementGroup}/${task.elementIndex}/${i}`,
                notify: !task.isSeparateItemsInModule && t.visited && !t.completed && t.hasQuestions,
            })),
        } : null;

        return (
            <PageLayout
                title={task.taskName}
                breadcrumb={getBreadCrumb(task.taskName, translate, clientConfig, task)}
                progress={task.showProgress ? task.progress : null}
                task={task}
                sideMenu={sidemenu}
                key={task.id}
            >
                <PageTitle title={`${translate('header.title')} - ${task.moduleName}`} />

                <TrackActivity label={`${task.elementGroup}/task`} patientProgramId={currentProgram.id} patientProgramElementId={task.patientProgramElementId} taskId={task.id} />
                <DelayedFetch dataCall={this.visitTask(task)} delaySeconds={clientConfig.appSetup.visitTaskDelay} />

                <MyTaskContent
                    patientProgramElementId={task.patientProgramElementId}
                    task={task}
                    ref={this.taskContentRef}
                />

                <TaskNavigation
                    className={classes.navigation}
                    task={task}
                    taskContentRef={this.taskContentRef}
                    readOnly={!currentProgram.isActiveProgram}
                />

                {clientConfig.appSetup.features.showPrintPageButton && (
                    <div className={classes.print}>
                        <Button onClick={() => window.print()}>{translate('task.printThisPage')}</Button>
                    </div>
                )}

            </PageLayout>
        );
    }
}

Task.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            elementGroup: PropTypes.string.isRequired,
            elementIndex: PropTypes.string.isRequired,
            subIndex: PropTypes.string,
        }).isRequired,
    }).isRequired,
    visitTask: PropTypes.func.isRequired,
    currentProgram: PropTypes.object.isRequired,
    clientConfig: PropTypes.object.isRequired,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
    currentProgram: state.programStore.currentProgram,
    clientConfig: state.auth.clientConfig,
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(ProgramStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Task));
